import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { RowButton } from 'src/app/panel-components/models/row-button';
import { LoadingOverlayStoreService } from '../../../services/stores/loading-overlay-store.service';
import { PropertyStoreService } from '../../../services/stores/property-store.service';
import { GroupStoreService } from '../../../services/stores/group-store.service';
import { TaskService } from '../../../services/task.service';
import { PhobosTaskTemplate } from '../../../domain/phobos-task-template';
import { Group } from '../../../domain/group';
import { AutoRunStoreService } from '../../../services/stores/auto-run-store.service';
import { AutoRunSchedule } from '../../../domain/auto-run-schedule';
import { TaskStage } from '../../../domain/task-stage';
import { RefreshDataState } from '../../../domain/refresh-data-state';
import { DropDownStoreService } from '../../../services/stores/drop-down-store.service';
import { DataStoreService } from '../../../services/data-store.service';
import { TaskTemplateService } from '../../../services/stores/task-template.service';
import { Table } from 'primeng/table';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PhobosTaskService } from '../../../services/phobos-task.service';
import { UserPermissions } from '../../../domain/security/user-permissions';
import { PermissionsStoreService } from '../../../services/stores/permission-store-service';
import { WorkflowType } from '../../../domain/enums/workflow-type';

@Component({
   selector: 'app-task-management',
   templateUrl: './task-management.component.html',
   styleUrls: ['./task-management.component.scss']
})
export class TaskManagementComponent implements OnInit, OnDestroy {
   isDialogVisible = false;
   tasks: PhobosTaskTemplate[] = [];
   isLoading: boolean = false;
   headingButtons: RowButton[];
   action: boolean = false;
   currentProp: number | undefined;
   currentProperty: number | undefined;
   groups: Group[] = [];
   autoRunSchedules: AutoRunSchedule[] = [];
   currentTaskTemplate?: PhobosTaskTemplate;
   searchValue: string | undefined;
   userPermissions: UserPermissions = new UserPermissions();
   aiInfoVisible = false;
   taskStatusInfoVisible = false;
   readonly WorkflowType = WorkflowType;
   hasMissingSchedules: boolean = false;
   missingSchedule?: boolean;

   protected readonly Array = Array;
   private subscriptions: Subscription = new Subscription();

   constructor(
      private loadingOverlayStore: LoadingOverlayStoreService,
      private taskService: TaskService,
      private propStore: PropertyStoreService,
      private groupStore: GroupStoreService,
      private autoRunStore: AutoRunStoreService,
      private taskTemplateStore: TaskTemplateService,
      private dataStoreService: DataStoreService,
      private dropDownStore: DropDownStoreService,
      private confirmation: ConfirmationService,
      private messages: MessageService,
      private phobosTaskService: PhobosTaskService,
      private permissionsStore: PermissionsStoreService
   ) {
      this.currentProp = 0;
      this.headingButtons = [
         new RowButton(
            'add_group',
            'Create Task',
            'fal fa-plus',
            [
               'p-button-rounded',
               'p-button-success',
               'p-button-outlined',
               'p-mr-2',
               'p-mb-2'
            ],
            ['p-col-12', 'p-text-right'],
            'right'
         )
      ];
   }

   ngOnInit(): void {
      this.subscriptions.add(
         this.loadingOverlayStore.LoadingState.subscribe((loadingState) => {
            this.isLoading = !(
               !loadingState.AutoRun &&
               !loadingState.PhobosTask &&
               !loadingState.PhobosTaskTemplates &&
               !loadingState.Groups
            );
         })
      );
      this.updateDropDownState(false);
      this.subscriptions.add(
         this.propStore.currentProperty.subscribe((currentProp) => {
            this.currentProp = currentProp?.id;
            this.currentProperty = currentProp?.id;
         })
      );
      this.subscriptions.add(
         this.taskTemplateStore.taskTemplates.subscribe((templates) => {
            this.tasks = templates.map((task) => {
               const groupNames =
                  task.stages
                     ?.map((stage) => this.getGroupNameForStage(stage))
                     ?.join(' ')
                     ?.toLowerCase() ?? '';

               return {
                  ...task,
                  groupNames,
                  missingSchedule: !task.autoRunScheduleId
               };
            });

            // Sort: Type (A-Z), Name (A-Z), then put inactive (AutoRun off) last
            this.tasks.sort((a, b) => {
               const typeA = this.handleTaskType(a.type).toLowerCase();
               const typeB = this.handleTaskType(b.type).toLowerCase();

               const nameA = a.name.toLowerCase();
               const nameB = b.name.toLowerCase();

               // 1. Sort by type
               if (typeA < typeB) return -1;
               if (typeA > typeB) return 1;

               // 2. Sort by name
               if (nameA < nameB) return -1;
               if (nameA > nameB) return 1;

               // 3. Sort inactive (AutoRun off) to the bottom
               if (a.isAutoRunEnabled === b.isAutoRunEnabled) return 0;
               return a.isAutoRunEnabled ? -1 : 1;
            });

            this.hasMissingSchedules = this.tasks.some(
               (task) => task.missingSchedule
            );
         })
      );
      this.subscriptions.add(
         this.autoRunStore.autoRunSchedules.subscribe((schedules) => {
            this.autoRunSchedules = schedules;
         })
      );
      this.subscriptions.add(
         this.groupStore.groups.subscribe((groups) => {
            this.groups = groups;
         })
      );

      this.subscriptions.add(
         this.permissionsStore.get().subscribe((p: UserPermissions) => {
            this.userPermissions = p ?? new UserPermissions();
         })
      );
   }

   ngOnDestroy(): void {
      this.subscriptions.unsubscribe();
   }

   clear(table: Table) {
      table.clear();
      this.searchValue = '';
   }

   toggleAutoRun(task: PhobosTaskTemplate) {
      if (!this.currentProp || this.isDialogVisible || this.isRetired(task)) return;

      const newStatus = !task.isAutoRunEnabled;

      this.autoRunStore
         .updateAutorunStatus(newStatus, this.currentProp, task.id)
         .subscribe((t) => {
            task.isAutoRunEnabled = t.isActive;
         });
   }
   canToggleAi(): boolean {
      const p = this.userPermissions;
      const isAccountOwner = p.isAccountOwner;
      const isBillingAdmin = !!p.isBillingAdmin;
      //const isTyallsConfig = !!p.taskManagement.update;
      return isAccountOwner || isBillingAdmin;
   }

   isRetired(task: PhobosTaskTemplate): boolean {
      return task?.isArchived === true;
   }

   isAiAvailable(task: PhobosTaskTemplate): boolean {
      return task?.isAiAvailable === true;
   }

   getAiTooltip(task: any): string {
      if (this.isRetired(task) || !this.isAiAvailable(task)) {
         return 'AI Unavailable';
      }
      return task.isEnabledAi ? 'AI Activated' : 'AI Available';
   }

   showTaskStatusInfo(): void {
      this.taskStatusInfoVisible = true;
   }

   showAiInfo(): void {
      this.aiInfoVisible = true;
   }

   onAiToggleRequested(task: PhobosTaskTemplate, nextValue: boolean) {
      const prevValue = !nextValue; // what it was before the click

      // Guard rails
      if (!this.canToggleAi() || this.isRetired(task) || !this.isAiAvailable(task)) {
         task.isEnabledAi = prevValue;
         return;
      }

      const acceptEnable = () => {
         this.phobosTaskService
            .setAiActive(task.id, this.currentProp!, true)
            .subscribe({
               next: () => {
                  task.isEnabledAi = true;
                  this.messages.add({
                     severity: 'success',
                     summary: 'AI Enabled',
                     detail: 'Billing has been notified.'
                  });
               },
               error: () => (task.isEnabledAi = prevValue)
            });
      };

      const acceptDisable = () => {
         this.phobosTaskService
            .setAiActive(task.id, this.currentProp!, false)
            .subscribe({
               next: () => {
                  task.isEnabledAi = false;
                  this.messages.add({
                     severity: 'success',
                     summary: 'AI Scheduled for Cancellation',
                     detail: 'AI will remain active until the current period ends.'
                  });
               },
               error: () => (task.isEnabledAi = prevValue)
            });
      };

      if (nextValue) {
         // ENABLE confirm
         this.confirmation.confirm({
            header: 'Enable AI for This Task?',
            message:
               'Monthly fee of AU$15 applies from activation through your current billing period. <br/>You can uncheck the box at anytime to cancel — changes take effect at your next billing cycle, and you retain full access until then.<br/><br/>Activate AI now?',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            accept: acceptEnable,
            reject: () => (task.isEnabledAi = prevValue)
         });
      } else {
         // DISABLE confirm
         this.confirmation.confirm({
            header: 'Disable AI?',
            message:
               'Are you sure you want to disable AI for this task? AI will remain active until the end of your current billing period.',
            acceptLabel: 'Yes, Disable AI',
            rejectLabel: 'No, Go Back',
            accept: acceptDisable,
            reject: () => (task.isEnabledAi = prevValue)
         });
      }
   }

   handleTaskType(taskType: WorkflowType): string {
      switch (taskType) {
         case WorkflowType.Daily:
            return 'Daily';
         case WorkflowType.NonDaily:
            return 'Non-daily';
         case WorkflowType.Private:
            return 'Private';
         case WorkflowType.Contracts:
            return 'Contracts';
         case WorkflowType.Form:
            return 'Form';
         default:
            return 'Daily';
      }
   }

   getTaskClass(taskType: WorkflowType): string {
      switch (taskType) {
         case WorkflowType.Daily:
            return 'schedule-tag-daily';
         case WorkflowType.NonDaily:
            return 'schedule-tag-non-daily';
         case WorkflowType.Private:
            return 'schedule-tag-private';
         case WorkflowType.Contracts:
            return 'schedule-tag-contracts';
         case WorkflowType.Form:
            return 'schedule-tag-form';
         default:
            return 'schedule-tag-daily';
      }
   }

   handleDisableEditButton(taskType: number): boolean {
      if (this.isDialogVisible) {
         return true;
      }
      switch (taskType) {
         case WorkflowType.Form:
            return true;
         default:
            return false;
      }
   }

   updateDropDownState(value: boolean): void {
      this.dropDownStore.setDropDownState(value);
   }

   formatDate(dateString: string): string {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
         return 'Invalid Date';
      }
      const options: Intl.DateTimeFormatOptions = {
         year: 'numeric',
         month: 'long',
         day: 'numeric'
      };
      return date.toLocaleDateString(undefined, options);
   }

   onDialogClose(): void {
      this.isDialogVisible = false;
      const refreshDate = new RefreshDataState();
      refreshDate.PhobosTaskTemplates = true;
      this.dataStoreService.refreshTaskData(refreshDate);
   }

   onDialogOpen(action: boolean, task?: PhobosTaskTemplate): void {
      this.action = action;
      this.currentTaskTemplate = task;
      this.isDialogVisible = true;
   }

   getGroupNameForStage(stage: TaskStage) {
      const group = this.groups.find((g) => g.id == stage.groupId);
      if (group) {
         return group.displayName;
      }
      return '';
   }

   filterTable(input: Event | null, table: Table) {
      if (!input) return;

      const value = (input.target as HTMLInputElement).value;
      table.filterGlobal(value, 'contains');
   }
   /*adding a comment to avoid release build failure*/
   getOrgInitial(name?: string | null): string {
      return (name ?? '').trim().charAt(0).toUpperCase();
   }
}
